import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { getChannel } from "@/mocks/channels";
import { formatDate } from "@/utils/display";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: Props) {
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setKeyword("");
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return articles.slice(0, 5);
    return articles
      .filter((a) => {
        const channel = getChannel(a.channel);
        const haystack = `${a.title} ${a.subtitle} ${a.excerpt} ${a.tags.join(" ")} ${
          channel?.name ?? ""
        }`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8);
  }, [keyword]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="站内搜索">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-foreground-950/50"
      />
      <div className="relative mx-auto mt-24 w-[92%] max-w-2xl overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-none animate-rise">
        <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3">
          <span className="flex h-6 w-6 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-lg" aria-hidden="true" />
          </span>
          <input
            ref={inputRef}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索档案标题、标签或栏目…"
            className="h-8 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-2">
          {results.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-foreground-500">
              没有找到相关档案，换个关键词试试
            </p>
          ) : (
            results.map((a) => {
              const channel = getChannel(a.channel);
              return (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  onClick={onClose}
                  className="flex items-start gap-3 rounded-md px-3 py-3 transition-colors hover:bg-background-100"
                >
                  <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img src={a.cover} alt={`${a.title} 配图`} className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{a.title}</p>
                    <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                      <span>{channel?.name}</span>
                      <span>{formatDate(a.publishedAt)}</span>
                    </p>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}